import React, { useEffect, useState } from 'react';
import {
  Brain,
  FolderKanban,
  Database,
  BookOpenText,
  Info,
  Save,
  FileJson,
  Globe,
  MinusCircle,
} from 'lucide-react';
import FilterableMultiSelect from '../../../common/FilterableMultiSelect';
import { ingestionService } from '../../../../../components/api/ingestionService';
import { knowledgebaseService } from '../../../../../components/api/knowledgebaseService';
import { AgentProps } from '../Interfaces/AgentProps';
import { agentService } from '../services/agentService';

const tenantId = '3fa85f64-5717-4562-b3fc-2c963f66afa6'; // Replace with dynamic value if needed
type Option = {
  label: string;
  value: string;
};
type Props = {
  agent?: AgentProps;
};
const AgentResources: React.FC<Props> = ({ agent }) => {
  const [loadingHelperKbs, setLoadingHelperKbs] = useState(false);

  const [selectedInputType, setSelectedInputType] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');
  const [jsonInput, setJsonInput] = useState('');

  const [selectedLLMs, setSelectedLLMs] = useState<string[]>([]);
  const [selectedVectorStores, setSelectedVectorStores] = useState<string[]>([]);
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);

  const [llmOptions, setLlmOptions] = useState([]);
  const [vectorOptions, setVectorOptions] = useState([]);

  const [inputReferenceKbEntityId, setInputReferenceKbEntityId] = useState('');
  const [inputReferenceKbEntityOptions, setInputReferenceKbEntityOptions] = useState<Option[]>([]);


  useEffect(() => {
    const fetchResources = async () => {
      const [llms, vectors, dbs] = await Promise.all([
        ingestionService.getLLMConfigurations(tenantId),
        ingestionService.getVectorStores(),
        ingestionService.loadDataSources(tenantId),
      ]);

      setLlmOptions(
        llms.map((llm: any) => ({
          label: `${llm.modelName} (${llm.region})`,
          value: llm.id,
        }))
      );

      setVectorOptions(
        vectors.map((vec: any) => ({
          label: vec.description,
          value: vec.id,
        }))
      );

      setDbOptions(
        dbs.map((ds: any) => ({
          label: `${ds.connectionName} (${ds.description})`,
          value: ds.id,
        }))
      );
    };

    fetchResources();

    if (agent) {
      setInputReferenceKbEntityId(agent.inputReferenceId || '');
      setSelectedHelperDatabases(agent.helperEntities || []);

      setSelectedInputType(
        agent.inputType === 1 ? 'entity' :
          agent.inputType === 2 ? 'workspace' :
            agent.inputType === 3 ? 'json' :
              'unspecified'
      );

      setSelectedEntity(agent.inputReferenceId || '');
      setJsonInput(agent.inputJsonFormat || '');
      setSelectedWorkspace(agent.inputReferenceId || '');
      setInputReferenceKbEntityId(agent.InputReferenceKbEntityId || '');

    }
  }, []);

  useEffect(() => {
    const loadKbEntityOptions = async () => {
      if (!selectedEntity) {
        setInputReferenceKbEntityOptions([]);
        return;
      }
      try {
        const all = await ingestionService.loadkbEntitiesList(); // or a filtered API
        const found = all.find((e: any) => e.id === selectedEntity);
        if (found && found.entities) {
          const options = found.entities.map((ent: any) => ({
            label: ent.title || ent.name || ent.id,
            value: ent.id,
          }));
          setInputReferenceKbEntityOptions(options);
        } else {
          setInputReferenceKbEntityOptions([]);
        }
      } catch (err) {
        console.error('Failed to load kb entity options:', err);
      }
    };

    loadKbEntityOptions();
  }, [selectedEntity]);

  const [workspaceEntitiesMap, setWorkspaceEntitiesMap] = useState<{ [workspaceId: string]: any[] }>({});
  const [selectedEntityFromWorkspace, setSelectedEntityFromWorkspace] = useState('');
  const [dbOptions, setDbOptions] = useState<{ label: string; value: string }[]>([]);


  const [workspaceOptions, setWorkspaceOptions] = useState<Option[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [knowledgeBaseOptions, setKnowledgeBaseOptions] = useState<Option[]>([]);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<string[]>([]);

  useEffect(() => {
    const loadWorkspaces = async () => {
      const data = await ingestionService.loadkbEntitiesList(); // /api/KnowledgeBase/kbEntitiesList
      setWorkspaceOptions(
        data.map((w: any) => ({
          label: w.title,
          value: w.id,
        }))
      );
      const map: any = {};
      data.forEach((w: any) => {
        map[w.id] = w.entities;
      });
      setWorkspaceEntitiesMap(map);
    };
    loadWorkspaces();
  }, []);
  useEffect(() => {
    const loadKBs = async () => {
      if (!selectedWorkspace) return;
      const data = await knowledgebaseService.getKnowledgeBasesBySourceId(selectedWorkspace); // /KnowledgeBase/GetKnowledgeBases
      setKnowledgeBaseOptions(data.map((kb: any) => ({
        label: `${kb.title} (${kb.entityName})`,
        value: kb.id,
      })));
    };
    loadKBs();
  }, [selectedWorkspace]);

  const [helperSelectedWorkspace, setHelperSelectedWorkspace] = useState('');
  const [helperKbOptions, setHelperKbOptions] = useState<Option[]>([]);
  const [selectedHelperKbs, setSelectedHelperKbs] = useState<string[]>([]);

  useEffect(() => {
    const loadHelperKbs = async () => {
      if (!helperSelectedWorkspace) return;
      const kbs = await knowledgebaseService.getKnowledgeBasesBySourceId(helperSelectedWorkspace);
      setHelperKbOptions(
        kbs.map((kb: any) => ({
          label: `${kb.title} (${kb.entityName})`,
          value: kb.id,
        }))
      );
    };
    loadHelperKbs();
  }, [helperSelectedWorkspace]);

  const [masterPrompt, setMasterPrompt] = useState('');

  enum AgentOutputType {
    NotSpecified = 0,
    Json = 1
  }

  const [outputType, setOutputType] = useState<AgentOutputType>(AgentOutputType.NotSpecified);
  const [outputJsonFormat, setOutputJsonFormat] = useState('');

  useEffect(() => {
    if (!agent) return;

    // Determine input type
    if (agent.inputType === 1) setSelectedInputType('entity');
    else if (agent.inputType === 2) setSelectedInputType('workspace');
    else if (agent.inputType === 3) setSelectedInputType('json');
    else setSelectedInputType('unspecified');

    setSelectedEntity(agent.inputReferenceId || '');
    setSelectedWorkspace(agent.inputReferenceId || '');
    setJsonInput(agent.inputJsonFormat || '');

    setSelectedLLMs(agent.llmConfigIds || []);
    setSelectedVectorStores(agent.vectorStoreIds || []);
    setSelectedKnowledgeBases(agent.knowledgeHubIds || []);

    setOutputType(agent.outputType ?? 0);
    setOutputJsonFormat(agent.outputJsonFormat || '');
    setMasterPrompt(agent.masterPrompt || '');

    // setSelectedDatabases(agent.databaseIds || []); // Optional: if you track separately
    // setSelectedHelperKbs(agent.helperKnowledgeHubIds || []);
    // setHelperSelectedWorkspace(agent.helperWorkspaceId || '');
    if (agent.knowledgeWorkSpaceID) {
      setKnowledgeWorkspaceId(agent.knowledgeWorkSpaceID);
    }
  }, [agent]);

 

  // useEffect(() => {
  //   const loadWorkspaces = async () => {
  //     const data = await ingestionService.loadkbEntitiesList(); // /api/KnowledgeBase/kbEntitiesList
  //     setWorkspaceOptions(data.map((w: any) => ({ label: w.title, value: w.id })));
  //   };
  //   loadWorkspaces();
  // }, []);


  const handleSave = async () => {
    if (!agent?.id) return;

    const payload = {
      agentId: agent.id,
      tenantID: tenantId,
      inputType:
        selectedInputType === 'entity'
          ? 1
          : selectedInputType === 'workspace'
            ? 2
            : selectedInputType === 'json'
              ? 3
              : 0, // unspecified
      inputReferenceId:
        selectedInputType === 'entity'
          ? selectedEntity
          : selectedInputType === 'workspace'
            ? knowledgeWorkspaceId
            : selectedInputType === 'json'
              ? undefined
              : undefined,

      inputReferenceKbEntityId: inputReferenceKbEntityId || null,
      inputJsonFormat: selectedInputType === 'json' ? jsonInput : '',
      llmConfigIds: selectedLLMs,
      vectorStoreIds: selectedVectorStores,
      outputType,
      outputJsonFormat,
      masterPrompt,
      helperEntities: selectedHelperDatabases,
      chatContext: '', // Optional: extend if needed
      ownerRoleIds: agent.ownerRoleIds || [], // fallback if present
      updatedBy: 'system', // Replace with current user if available
      knowledgeWorkspaceId: knowledgeWorkspaceId,
      knowledgeHubIds: helperKnowledgeHubIds,
    };

    try {
      await agentService.updateConfiguration(payload);
      alert('Agent configuration updated successfully!');
    } catch (err) {
      console.error('Error updating agent config:', err);
      alert('Failed to update configuration.');
    }
  };
  const [selectedHelperDatabases, setSelectedHelperDatabases] = useState<string[]>([]);
  const [helperKnowledgeOptions, setHelperKnowledgeOptions] = useState<Option[]>([]);

  const [knowledgeWorkspaceId, setKnowledgeWorkspaceId] = useState('');
  const [helperKnowledgeHubIds, setHelperKnowledgeHubIds] = useState<string[]>([]);
  const [allKnowledgeBases, setAllKnowledgeBases] = useState<any[]>([]); // Replace `any` with KB type


  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const workspaces = await ingestionService.loadkbEntitiesList()// or your actual API
        setWorkspaceOptions(
          workspaces.map((w: any) => ({
            label: w.title,
            value: w.id,
          }))
        );
      } catch (err) {
        console.error('Error loading workspaces', err);
      }
    };

    loadWorkspaces();
  }, []);

  useEffect(() => {
    const fetchHelperKnowledgeBases = async () => {
      if (!knowledgeWorkspaceId) {
        setHelperKbOptions([]);
        return;
      }

      try {
        const result = await knowledgebaseService.getKnowledgeBasesBySourceId(knowledgeWorkspaceId);
        setHelperKbOptions(
          result.map((kb: any) => ({
            label: `${kb.title} (${kb.entityName})`,
            value: kb.id,
          }))
        );
      } catch (err) {
        console.error('Failed to fetch helper knowledge bases:', err);
        setHelperKbOptions([]);
      }
    };

    fetchHelperKnowledgeBases();
  }, [knowledgeWorkspaceId]);

   useEffect(() => {
  const fetchHelperKnowledgeBases = async () => {
    if (!knowledgeWorkspaceId) return;

    const result = await knowledgebaseService.getKnowledgeBasesBySourceId(knowledgeWorkspaceId);
    const options = result.map((kb: any) => ({
      label: `${kb.title} (${kb.entityName})`,
      value: kb.id,
    }));
    setHelperKbOptions(options);

    if (agent?.knowledgeHubIds?.length) {
      const validIds = agent.knowledgeHubIds.filter((id: string) =>
        options.some((opt: { value: string; label: string }) => opt.value === id)
      );
      setHelperKnowledgeHubIds(validIds);
    }
  };

  fetchHelperKnowledgeBases();
}, [knowledgeWorkspaceId, agent]);



  return (
    <div className="p-8 text-white max-w-7xl mx-auto flex flex-col lg:flex-row gap-10 min-h-[calc(100vh-4rem)]">
      {/* Left Panel */}
      <div className="flex-1 space-y-8">
        <div className="flex items-center gap-2 text-xl font-semibold">
          <FolderKanban className="w-5 h-5 text-indigo-500" />
          Agent Resources
        </div>

        {/* Input Resource Options */}

        {/* Master Prompt Section */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-4">
          <h2 className="text-sm font-bold text-gray-200">Master Prompt</h2>
          <p className="text-sm text-gray-400">
            This master prompt provides default behavior or system instructions for the agent. It will be used as the base for all interactions unless overridden.
          </p>
          <textarea
            value={masterPrompt}
            onChange={(e) => setMasterPrompt(e.target.value)}
            className="w-full bg-neutral-900 text-sm text-gray-300 p-3 border border-neutral-700 rounded-md"
            rows={6}
            placeholder="e.g., You are a helpful assistant trained on internal policy documents..."
          />
        </div>
        {/* Input Resources */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-4">
          <h2 className="text-sm font-bold text-gray-200">Input Resources</h2>
          <p className="text-sm text-gray-400">Choose one primary input type for the agent:</p>
          <div className="space-y-3 text-sm">
            {['entity', 'workspace', 'json', 'unspecified'].map((type) => (
              <div key={type} className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={selectedInputType === type}
                    onChange={() => {
                      setSelectedInputType(type);
                      // Reset related fields
                      setSelectedEntity('');
                      setSelectedWorkspace('');
                      setSelectedKnowledgeBases([]);
                      setJsonInput('');
                      setInputReferenceKbEntityId('');
                    }}
                  />
                  {type === 'entity' && <Database className="w-4 h-4 text-gray-400" />}
                  {type === 'workspace' && <BookOpenText className="w-4 h-4 text-gray-400" />}
                  {type === 'api' && <Globe className="w-4 h-4 text-gray-400" />}
                  {type === 'json' && <FileJson className="w-4 h-4 text-gray-400" />}
                  {type === 'unspecified' && <MinusCircle className="w-4 h-4 text-gray-400" />}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </label>

                {/* ENTITY TYPE */}
                {selectedInputType === 'entity' && type === 'entity' && (
                  <>
                    <select
                      className="bg-neutral-800 text-sm px-3 py-2 rounded w-full"
                      value={selectedEntity}
                      onChange={(e) => setSelectedEntity(e.target.value)}
                    >
                      <option value="">Select Entity...</option>
                      {dbOptions.map((ds) => (
                        <option key={ds.value} value={ds.value}>
                          {ds.label}
                        </option>
                      ))}
                    </select>

                    {/* New: InputReferenceKbEntityId (child of selectedEntity) */}
                    {selectedEntity && inputReferenceKbEntityOptions.length > 0 && (
                      <select
                        className="bg-neutral-800 text-sm px-3 py-2 rounded w-full mt-2"
                        value={inputReferenceKbEntityId}
                        onChange={(e) => setInputReferenceKbEntityId(e.target.value)}
                      >
                        <option value="">Select Kb Entity...</option>
                        {inputReferenceKbEntityOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                  </>
                )}

                {/* WORKSPACE TYPE */}
                {selectedInputType === 'workspace' && type === 'workspace' && (
                  <div className="space-y-2">
                    {/* Parent Dropdown */}
                    <label className="text-sm text-gray-300">Select Workspace</label>
                    <select
                      className="bg-neutral-800 text-sm px-3 py-2 rounded w-full mt-2"
                      value={knowledgeWorkspaceId}
                      onChange={(e) => {
                        setKnowledgeWorkspaceId(e.target.value);
                        setHelperKnowledgeHubIds([]); // clear previous child selection
                      }}
                    >
                      <option value="">Select Workspace...</option>
                      {workspaceOptions.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>

                    {/* Child Multiselect */}
                    {knowledgeWorkspaceId && helperKbOptions.length > 0 && (
                      <FilterableMultiSelect
                        label="Helper Knowledge Bases"
                        options={helperKbOptions}
                        selected={helperKnowledgeHubIds}
                        setSelected={setHelperKnowledgeHubIds}
                      />
                    )}
                  </div>

                )}

                {/* JSON TYPE */}
                {selectedInputType === 'json' && type === 'json' && (
                  <textarea
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    className="w-full bg-neutral-900 text-sm text-gray-300 p-3 border border-neutral-700 rounded-md mt-2"
                    rows={6}
                    placeholder="Paste your JSON structure here..."
                  />
                )}
              </div>
            ))}


          </div>
        </div>

        {/* Helper Resources */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-6">
          <h2 className="text-sm font-bold text-gray-200">Helper Resources</h2>
          <p className="text-sm text-gray-400">Optional resources to enhance the agent's capabilities:</p>

          <FilterableMultiSelect
            label="LLMs"
            options={llmOptions}
            selected={selectedLLMs}
            setSelected={setSelectedLLMs}
          />
          <FilterableMultiSelect
            label="Vector Stores"
            options={vectorOptions}
            selected={selectedVectorStores}
            setSelected={setSelectedVectorStores}
          />
          <FilterableMultiSelect
            label="Entities (Databases)"
            options={dbOptions}
            selected={selectedHelperDatabases}
            setSelected={setSelectedHelperDatabases}
          />


          <div className="space-y-3">
            <label className="text-xs text-gray-400 font-semibold">Knowledge Workspace</label>
            <select
              className="bg-neutral-800 text-sm px-3 py-2 rounded w-full mt-2"
              value={knowledgeWorkspaceId}
              onChange={(e) => {
                setKnowledgeWorkspaceId(e.target.value);
                setHelperKnowledgeHubIds([]);
              }}
            >
              <option value="">Select Helper Workspace...</option>
              {workspaceOptions.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>


            {knowledgeWorkspaceId && helperKbOptions.length > 0 && (
              <FilterableMultiSelect
                label="Helper Knowledge Bases"
                options={helperKbOptions}
                selected={helperKnowledgeHubIds}
                setSelected={setHelperKnowledgeHubIds}
              />
            )}

          </div>


        </div>

        {/* Output Configuration */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-4">
          <h2 className="text-sm font-bold text-gray-200">Output Configuration</h2>
          <p className="text-sm text-gray-400">
            Specify how you want the agent to return results. Use JSON format if the output will be consumed programmatically.
          </p>

          {/* Output Type Dropdown */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Output Type</label>
            <select
              className="w-full bg-neutral-800 text-sm px-3 py-2 rounded-md border border-neutral-700 text-white"
              value={outputType}
              onChange={(e) => setOutputType(parseInt(e.target.value))}
            >
              <option value={AgentOutputType.NotSpecified}>Not Specified</option>
              <option value={AgentOutputType.Json}>JSON</option>
            </select>
          </div>

          {/* JSON Format Textarea */}
          {outputType === AgentOutputType.Json && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Output JSON Format</label>
              <textarea
                value={outputJsonFormat}
                onChange={(e) => setOutputJsonFormat(e.target.value)}
                className="w-full bg-neutral-900 text-sm text-gray-300 p-3 border border-neutral-700 rounded-md"
                rows={6}
                placeholder={`e.g. {
  "status": "approved",
  "reason": "Credit score above threshold",
  "recommended_limit": 50000
}`}
              />
            </div>
          )}
        </div>


        <div>
          <button onClick={handleSave} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow-sm">
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Right Info Panel */}
      <div className="w-full lg:w-80 bg-neutral-900 border border-neutral-800 rounded-lg p-5 text-sm text-gray-300 space-y-4">
        <div className="flex items-center gap-2 text-white text-base font-medium">
          <Info className="w-4 h-4 text-indigo-400" />
          About Resources
        </div>
        <p>
          Resources define what your agent can access during execution. Use <b>Input Resources</b> for core context and <b>Helper Resources</b> to enhance performance.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="inline-flex items-center gap-1 text-indigo-300">
              <Brain className="w-3 h-3" /> LLMs
            </span>{' '}
            — Reasoning and conversation generation
          </li>
          <li>
            <span className="inline-flex items-center gap-1 text-indigo-300">
              <BookOpenText className="w-3 h-3" /> Vector Stores
            </span>{' '}
            — RAG-based document lookups
          </li>
          <li>
            <span className="inline-flex items-center gap-1 text-indigo-300">
              <Database className="w-3 h-3" /> Databases
            </span>{' '}
            — Structured data access
          </li>
        </ul>
        <div className="pt-4 border-t border-neutral-800 text-xs text-gray-400 space-y-2">
          <p className="font-semibold text-white">📘 Real-world Example</p>
          <p>
            You configure the agent with:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <b>Entity</b>: <span className="text-gray-300">Customer Table (PostgreSQL)</span>
            </li>
            <li>
              <b>Knowledge Base</b>: <span className="text-gray-300">Loan Policy Documents</span> from workspace <i>Finance Compliance</i>
            </li>
            <li>
              <b>Vector Store</b>: <span className="text-gray-300">Qdrant (gov-vector-data)</span>
            </li>
            <li>
              <b>LLM</b>: <span className="text-gray-300">GPT-4o-mini (Azure OpenAI)</span>
            </li>
            <li>
              <b>Master Prompt</b>: <i>"You are a banking compliance assistant helping validate loan approvals. Always rely on the uploaded policy documents and customer records. Never assume or hallucinate."</i>
            </li>
          </ul>

          <p className="text-[11px] text-gray-500 pt-2">
            🧠 The agent retrieved structured data from the database, used the vector store to semantically search loan policies, and applied the LLM to synthesize a contextual decision — all under your configured prompt and resource scope.
          </p>
        </div>

        <p className="text-[11px] text-gray-500 pt-2">
          Changes are applied when the agent is rebuilt. You can update these configurations anytime.
        </p>
      </div>
    </div>
  );
};

export default AgentResources;



// import React, { useState } from 'react';
// import {
//   Brain,
//   FolderKanban,
//   Database,
//   BookOpenText,
//   Info,
//   Save,
//   FileJson,
//   Globe,
//   MinusCircle,
// } from 'lucide-react';
// import FilterableMultiSelect from '../../../common/FilterableMultiSelect';

// const AgentResources = () => {
//   const [selectedInputType, setSelectedInputType] = useState('');
//   const [selectedEntity, setSelectedEntity] = useState('');
//   const [selectedWorkspace, setSelectedWorkspace] = useState('');
//   const [jsonInput, setJsonInput] = useState('');

//   const [selectedLLMs, setSelectedLLMs] = useState<string[]>([]);
//   const [selectedVectorStores, setSelectedVectorStores] = useState<string[]>([]);
//   const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
//   const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);

//   const llmOptions = [
//     { label: 'GPT-4o', value: 'gpt-4o' },
//     { label: 'Claude 3', value: 'claude-3' },
//     { label: 'Mistral 7B', value: 'mistral-7b' },
//   ];

//   const vectorOptions = [
//     { label: 'Customer Embeddings (Pinecone)', value: 'vector1' },
//     { label: 'Legal Docs (Weaviate)', value: 'vector2' },
//   ];

//   const dbOptions = [
//     { label: 'Production DB (PostgreSQL)', value: 'db1' },
//     { label: 'Analytics DB (Clickhouse)', value: 'db2' },
//   ];

//   const workspaceOptions = [
//     { label: 'Product Support Docs', value: 'kw1' },
//     { label: 'Internal Knowledge Base', value: 'kw2' },
//   ];

//   return (
//     <div className="p-8 text-white max-w-7xl mx-auto flex flex-col lg:flex-row gap-10 min-h-[calc(100vh-4rem)]">
//       {/* Left Side: Main Content */}
//       <div className="flex-1 space-y-8">
//         {/* Title */}
//         <div className="flex items-center gap-2 text-xl font-semibold">
//           <FolderKanban className="w-5 h-5 text-indigo-500" />
//           Agent Resources
//         </div>

//         {/* Section: Input Resources */}
//         <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-4">
//           <h2 className="text-sm font-bold text-gray-200">Input Resources</h2>
//           <p className="text-sm text-gray-400">Choose one primary input type for the agent:</p>

//           <div className="space-y-3 text-sm">
//             {/* Entity */}
//             <label className="flex items-center gap-2">
//               <input
//                 type="radio"
//                 checked={selectedInputType === 'entity'}
//                 onChange={() => setSelectedInputType('entity')}
//               />
//               <Database className="w-4 h-4 text-gray-400" />
//               Entity
//             </label>
//             {selectedInputType === 'entity' && (
//               <select
//                 className="bg-neutral-800 text-sm px-3 py-2 rounded w-full"
//                 onChange={(e) => setSelectedEntity(e.target.value)}
//               >
//                 <option value="">Select entity...</option>
//                 <option value="employee">Employee Table</option>
//                 <option value="customers">Customer Table</option>
//               </select>
//             )}

//             {/* Workspace */}
//             <label className="flex items-center gap-2">
//               <input
//                 type="radio"
//                 checked={selectedInputType === 'workspace'}
//                 onChange={() => setSelectedInputType('workspace')}
//               />
//               <BookOpenText className="w-4 h-4 text-gray-400" />
//               Knowledge Workspace
//             </label>
//             {selectedInputType === 'workspace' && (
//               <select
//                 className="bg-neutral-800 text-sm px-3 py-2 rounded w-full"
//                 onChange={(e) => setSelectedWorkspace(e.target.value)}
//               >
//                 <option value="">Select workspace...</option>
//                 <option value="kw1">Product Docs</option>
//                 <option value="kw2">Support Files</option>
//               </select>
//             )}

//             {/* API */}
//             <label className="flex items-center gap-2">
//               <input
//                 type="radio"
//                 checked={selectedInputType === 'api'}
//                 onChange={() => setSelectedInputType('api')}
//               />
//               <Globe className="w-4 h-4 text-gray-400" />
//               API
//             </label>

//             {/* JSON */}
//             <label className="flex items-center gap-2">
//               <input
//                 type="radio"
//                 checked={selectedInputType === 'json'}
//                 onChange={() => setSelectedInputType('json')}
//               />
//               <FileJson className="w-4 h-4 text-gray-400" />
//               Provide JSON Format
//             </label>
//             {selectedInputType === 'json' && (
//               <textarea
//                 value={jsonInput}
//                 onChange={(e) => setJsonInput(e.target.value)}
//                 className="w-full bg-neutral-900 text-sm text-gray-300 p-3 border border-neutral-700 rounded-md mt-2"
//                 rows={6}
//                 placeholder="Paste your JSON structure here..."
//               />
//             )}

//             {/* Unspecified */}
//             <label className="flex items-center gap-2">
//               <input
//                 type="radio"
//                 checked={selectedInputType === 'unspecified'}
//                 onChange={() => setSelectedInputType('unspecified')}
//               />
//               <MinusCircle className="w-4 h-4 text-gray-400" />
//               Not Specified
//             </label>
//           </div>
//         </div>

//         {/* Section: Helper Resources */}
//         <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 space-y-6">
//           <h2 className="text-sm font-bold text-gray-200">Helper Resources</h2>
//           <p className="text-sm text-gray-400">Optional resources to enhance the agent's capabilities:</p>

//           <FilterableMultiSelect
//             label="LLMs"
//             options={llmOptions}
//             selected={selectedLLMs}
//             setSelected={setSelectedLLMs}
//           />
//           <FilterableMultiSelect
//             label="Vector Stores"
//             options={vectorOptions}
//             selected={selectedVectorStores}
//             setSelected={setSelectedVectorStores}
//           />
//           <FilterableMultiSelect
//             label="Databases"
//             options={dbOptions}
//             selected={selectedDatabases}
//             setSelected={setSelectedDatabases}
//           />
//           <FilterableMultiSelect
//             label="Knowledge Workspaces"
//             options={workspaceOptions}
//             selected={selectedWorkspaces}
//             setSelected={setSelectedWorkspaces}
//           />
//         </div>

//         {/* Save Button */}
//         <div>
//           <button className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow-sm">
//             <Save className="w-4 h-4" />
//             Save Changes
//           </button>
//         </div>
//       </div>

//       {/* Right Side: Info Panel */}
//       <div className="w-full lg:w-80 bg-neutral-900 border border-neutral-800 rounded-lg p-5 text-sm text-gray-300 space-y-4">
//         <div className="flex items-center gap-2 text-white text-base font-medium">
//           <Info className="w-4 h-4 text-indigo-400" />
//           About Resources
//         </div>
//         <p>
//           Resources define what your agent can access during execution. Use <b>Input Resources</b> for core context and <b>Helper Resources</b> to enhance performance.
//         </p>
//         <ul className="list-disc pl-5 space-y-1">
//           <li>
//             <span className="inline-flex items-center gap-1 text-indigo-300">
//               <Brain className="w-3 h-3" /> LLMs
//             </span>{' '}
//             — Reasoning and conversation generation
//           </li>
//           <li>
//             <span className="inline-flex items-center gap-1 text-indigo-300">
//               <BookOpenText className="w-3 h-3" /> Vector Stores
//             </span>{' '}
//             — RAG-based document lookups
//           </li>
//           <li>
//             <span className="inline-flex items-center gap-1 text-indigo-300">
//               <Database className="w-3 h-3" /> Databases
//             </span>{' '}
//             — Structured data access
//           </li>
//           <li>
//             <span className="inline-flex items-center gap-1 text-indigo-300">
//               <FolderKanban className="w-3 h-3" /> Knowledge Workspaces
//             </span>{' '}
//             — Indexed background docs
//           </li>
//         </ul>
//         <p className="text-[11px] text-gray-500 pt-2">
//           Changes are applied when the agent is rebuilt. You can update these configurations anytime.
//         </p>
//       </div>
//     </div>
//   );
// };

// export default AgentResources;
